import isRelativeUrl from 'is-relative-url';
import { CSSProperties, FC } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { getWixResizedImage } from '../../lib';

import { TriggerOpts } from '../../types';

type TriggerProps = TriggerOpts & {
  onClick?: () => void;
};

export const Trigger: FC<TriggerProps> = ({
  width,
  height,
  shiftX,
  shiftY,
  imageUrl: rawImageUrl,
  animation,
  position,
  rotate,
  onClick,
}) => {
  const getPosition = (): CSSProperties => {
    let top;
    let bottom;
    let right;
    let left;

    if (position.includes('top')) {
      top = `${shiftY}px`;
    }

    if (position.includes('bottom')) {
      bottom = `${shiftY}px`;
    }

    if (position.includes('right')) {
      right = `${shiftX}px`;
    }

    if (position.includes('left')) {
      left = `${shiftX}px`;
    }

    return { top, left, right, bottom };
  };

  const imageUrl = isRelativeUrl(rawImageUrl) ? getWixResizedImage(rawImageUrl, 300, 300) : rawImageUrl;

  return createPortal(
    <TriggerContainer
      style={{
        width,
        height,
        backgroundImage: `url(${imageUrl})`,
        ...getPosition(),
      }}
      rotate={rotate}
      animation={animation}
      onClick={onClick}
    />,
    document.body
  );
};

const createShakeKeyframes = (rotate: number) => css`
  @keyframes shake-${rotate} {
    0% {
      transform: scale3d(1, 1, 1) rotate(${rotate}deg);
    }

    2%,
    4% {
      transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, ${rotate - 5}deg);
    }

    6%,
    10%,
    14%,
    18% {
      transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, ${rotate + 5}deg);
    }

    8%,
    12%,
    16% {
      transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, ${rotate - 5}deg);
    }

    20% {
      transform: scale3d(1, 1, 1) rotate(${rotate}deg);
    }
  }
`;

const createZoomKeyframes = (rotate: number) => css`
  @keyframes zoom-${rotate} {
    0% {
      transform: scale3d(1, 1, 1) rotate(${rotate}deg);
    }

    100% {
      transform: scale3d(1.1, 1.1, 1.1) rotate(${rotate}deg);
    }
  }
`;

const TriggerContainer = styled.div<{ rotate: number; animation: boolean }>`
  position: fixed;
  cursor: pointer;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 100001;
  transform: scale3d(1, 1, 1) rotate(${(p) => p.rotate}deg);

  ${(p) => css`
    ${createZoomKeyframes(p.rotate)};
  `};

  ${(p) =>
    p.animation &&
    css`
      ${createShakeKeyframes(p.rotate)};
      animation: 5s linear 0s infinite;
      animation-name: shake-${p.rotate};
    `};

  :hover {
    animation-duration: 0.1s;
    animation-timing-function: ease-in-out;
    animation-name: zoom-${(p) => p.rotate};
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }
`;
